import React from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Stack,
  Typography
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  HourglassEmpty as HourglassIcon,
  Settings as SettingsIcon,
  WarningAmber as WarningAmberIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const normalizeStatus = (value) => String(value || '').trim().toLowerCase();

const getStatusMeta = (value, expected) => {
  const normalized = normalizeStatus(value);
  const isReady = normalized === expected;

  return {
    value: normalized || '—',
    color: isReady ? 'success' : 'warning',
    icon: isReady ? <CheckCircleIcon fontSize="small" /> : <HourglassIcon fontSize="small" />,
    isReady
  };
};

const StripeAccountStatusCard = ({ user, variant = 'card' }) => {
  const { t } = useTranslation('financial');

  const onboardingStatus = normalizeStatus(
    user?.stripe_onboarding_status || user?.stripeOnboardingStatus || user?.stripe_onboardingStatus
  );
  const connectStatus = normalizeStatus(
    user?.stripe_connect_status || user?.stripeConnectStatus || user?.stripe_connectStatus
  );

  const onboardingMeta = getStatusMeta(onboardingStatus, 'completed');
  const connectMeta = getStatusMeta(connectStatus, 'enabled');
  const isReady = onboardingMeta.isReady && connectMeta.isReady;

  const formatStatusValue = (value) => {
    const normalized = normalizeStatus(value);
    if (!normalized || normalized === '—') {
      return t('stripeAccountStatus.status.unknown', '—');
    }
    return t(`stripeAccountStatus.status.${normalized}`, normalized);
  };

  return (
    <Accordion
      defaultExpanded={variant !== 'banner'}
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid rgba(15, 23, 42, 0.08)',
        boxShadow: '0 12px 28px rgba(15, 23, 42, 0.08)',
        background: isReady
          ? 'linear-gradient(135deg, rgba(46,125,50,0.08), rgba(255,255,255,0.96))'
          : 'linear-gradient(135deg, rgba(245,124,0,0.08), rgba(255,255,255,0.96))',
        '& .MuiAccordionSummary-root': {
          minHeight: 70,
          padding: variant === 'banner' ? 1.2 : 2
        }
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="stripe-account-status-content"
        id="stripe-account-status-header"
        sx={{ alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
          <Box>
            <Typography
              variant={variant === 'banner' ? 'h5' : 'h4'}
              sx={{ fontWeight: 900, color: '#0f172a', letterSpacing: '0.02em' }}
            >
              {t('stripeAccountStatus.title', 'Statut Stripe du compte')}
            </Typography>
            <Typography variant="body1" sx={{ mt: 0.5, color: 'text.secondary', fontWeight: 600 }}>
              {isReady
                ? t('stripeAccountStatus.readyDescription', 'Les statuts Stripe du compte sont complets et actifs.')
                : t('stripeAccountStatus.incompleteDescription', 'Le compte Stripe doit encore être complété depuis la page financière.')}
            </Typography>
          </Box>
        </Box>

        {!isReady && (
          <WarningAmberIcon
            sx={{
              color: '#ff9800',
              marginLeft: 1,
              fontSize: '1.8rem',
              width: '1.8rem',
              height: '1.8rem',
              fontWeight: 900
            }}
            fontSize="inherit"
          />
        )}
      </AccordionSummary>

      <AccordionDetails sx={{ p: variant === 'banner' ? 2 : 2.5 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} useFlexGap flexWrap="wrap">
            <Chip
              icon={onboardingMeta.icon}
              label={`${t('stripeAccountStatus.onboarding', 'Onboarding')}: ${formatStatusValue(onboardingMeta.value)}`}
              color={onboardingMeta.color}
              variant={onboardingMeta.isReady ? 'filled' : 'outlined'}
            />
            <Chip
              icon={connectMeta.icon}
              label={`${t('stripeAccountStatus.connect', 'Compte Connect')}: ${formatStatusValue(connectMeta.value)}`}
              color={connectMeta.color}
              variant={connectMeta.isReady ? 'filled' : 'outlined'}
            />
            <Chip
              icon={isReady ? <CheckCircleIcon fontSize="small" /> : <ErrorIcon fontSize="small" />}
              label={isReady
                ? t('stripeAccountStatus.ready', 'Compte prêt')
                : t('stripeAccountStatus.toComplete', 'Compte à compléter')}
              color={isReady ? 'success' : 'warning'}
            />
          </Stack>

          {!isReady && (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              {t('stripeAccountStatus.actionHint', 'Les informations Stripe ne sont pas encore complètes. Ouvrez la page financière pour finaliser le compte.')}
            </Alert>
          )}

          <Button
            component={Link}
            to="/financial-owner"
            variant={isReady ? 'outlined' : 'contained'}
            startIcon={<SettingsIcon />}
            sx={{ alignSelf: 'flex-start' }}
          >
            {t('stripeAccountStatus.action', 'Ouvrir la page financière')}
          </Button>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default StripeAccountStatusCard;